import type {
  AutomationDeployClassDto,
  AutomationDeployDto,
  AutomationDeployMethodDto,
  AutomationDeployNoteDto,
  AutomationDeployState,
} from '@tms/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Play, Rocket } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../../../components/layout/PageHeader';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  EntityKey,
  ErrorState,
  SkeletonRows,
} from '../../../components/ui';
import { useErrorText, useFormat, useT } from '../../../i18n';
import { api } from '../../../lib/api-client';
import { cn } from '../../../lib/cn';
import { queryKeys } from '../../../lib/query-keys';
import { usePermissions } from '../../auth/permissions';

const deploysKey = [...queryKeys.automation.all, 'deploys'] as const;
/** A run started for a deploy finishes in minutes: the page follows it without a reload. */
const REFRESH_MS = 30_000;

type ClassStatus = AutomationDeployClassDto['status'];

const STATUS_STYLES: Record<ClassStatus, string> = {
  passed: 'bg-pass-soft text-pass',
  failed: 'bg-fail-soft text-fail',
  broken: 'bg-fail-soft text-fail',
  skipped: 'bg-block-soft text-block',
  missing: 'bg-sunken text-ink-muted',
  pending: 'bg-accent-soft text-accent-fg',
  none: 'bg-sunken text-ink-muted',
};

const STATE_STYLES: Record<AutomationDeployState, string> = {
  waiting: 'bg-accent-soft text-accent-fg',
  started: 'bg-pass-soft text-pass',
  noTests: 'bg-block-soft text-block',
  notStarted: 'bg-sunken text-ink-muted',
};

/** Changes that leave behaviour as it was: said apart from the ones to check. */
const QUIET_NOTES = new Set(['logging', 'comments', 'empty']);
const NOTE_KINDS = [
  'migration',
  'kafka',
  'job',
  'temporal',
  'server',
  'texts',
  'config',
  'deps',
  'removed',
  'unreached',
  'other',
  'logging',
  'comments',
  'empty',
] as const;

const StatusBadge = ({ status }: { status: ClassStatus }) => {
  const { t } = useT();
  const label =
    status === 'pending' || status === 'none'
      ? t(`automation.deploys.status.${status}`)
      : t(`automation.caseLinks.result.${status}`);
  return <Badge className={STATUS_STYLES[status]}>{label}</Badge>;
};

const ClassRow = ({
  item,
  deploy,
}: {
  item: AutomationDeployClassDto;
  deploy: AutomationDeployDto;
}) => {
  const { t } = useT();
  const run = deploy.runs.find((r) => r.projectId === item.projectId && r.pipelineId);
  return (
    <li className="flex flex-col gap-1 px-3 py-2">
      <div className="flex flex-wrap items-center gap-2 text-[13px]">
        <StatusBadge status={item.status} />
        <span className="font-medium text-ink">{item.displayName ?? item.className}</span>
        <span className="text-[12px] text-ink-subtle">
          {item.projectName} · {item.className} ·{' '}
          {t(`automation.deploys.confidence.${item.confidence}`)}
        </span>
        {item.async ? (
          <Badge className="bg-block-soft text-block" title={t('automation.deploys.asyncHint')}>
            {t('automation.deploys.async')}
          </Badge>
        ) : null}
        {run ? (
          <Link
            to="/automation/runs/$runId"
            params={{ runId: run.pipelineId as string }}
            search={{ project: run.projectId }}
            className="text-[12px] text-accent hover:underline"
          >
            #{run.pipelineId}
          </Link>
        ) : null}
      </div>
      {item.via[0] ? (
        <span
          className="break-words font-mono text-[11px] text-ink-subtle"
          title={item.via.join('\n')}
        >
          {item.via[0]}
          {item.via.length > 1 ? ` · +${item.via.length - 1}` : ''}
          {item.via[0].includes(' → ') && item.testMethods.length === 0
            ? ` · ${t('automation.deploys.setupOnly')}`
            : ''}
        </span>
      ) : null}
      {item.cases.length ? (
        <div className="flex flex-wrap gap-1.5">
          {item.cases.map((c) => (
            <Link
              key={c.caseKey}
              to="/projects/$projectCode/test-cases/$caseKey"
              params={{ projectCode: c.projectCode, caseKey: c.caseKey }}
              className={cn(
                'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[12px] hover:underline',
                STATUS_STYLES[c.status],
              )}
              title={c.title}
            >
              <EntityKey>{c.caseKey}</EntityKey>
              <span className="max-w-64 truncate">{c.title}</span>
            </Link>
          ))}
        </div>
      ) : (
        <span className="text-[12px] text-ink-subtle">{t('automation.deploys.noCases')}</span>
      )}
    </li>
  );
};

const MethodLine = ({ method }: { method: AutomationDeployMethodDto }) => {
  const { t } = useT();
  return (
    <li className="flex flex-wrap items-center gap-x-2 text-[12px]">
      <span className="font-mono text-ink">{method.name}</span>
      {method.async ? (
        <Badge className="bg-block-soft text-block" title={t('automation.deploys.asyncHint')}>
          {t('automation.deploys.async')}
        </Badge>
      ) : null}
      <span className="break-words font-mono text-[11px] text-ink-subtle">
        {method.chain.join(' → ')}
        {method.direct ? '' : ` · ${t('automation.deploys.confidence.medium')}`}
      </span>
    </li>
  );
};

/** What the change reached with no autotest on it, and what reaches no API method. */
const Reach = ({ deploy }: { deploy: AutomationDeployDto }) => {
  const { t } = useT();
  const untested = deploy.methods.filter((m) => m.tests === 0);
  const groups = new Map<string, AutomationDeployNoteDto[]>();
  for (const note of deploy.notes) groups.set(note.kind, [...(groups.get(note.kind) ?? []), note]);
  const kinds = NOTE_KINDS.filter((k) => groups.has(k));
  const loud = kinds.filter((k) => !QUIET_NOTES.has(k));
  const quiet = kinds.filter((k) => QUIET_NOTES.has(k));
  const sample = (kind: string) => {
    const notes = groups.get(kind) ?? [];
    const shown = notes.slice(0, 3).map((n) => (n.symbol ? `${n.symbol} · ${n.path}` : n.path));
    return shown.join(', ') + (notes.length > 3 ? ` · +${notes.length - 3}` : '');
  };
  return (
    <>
      {deploy.linkedBy === 'method' && untested.length ? (
        <div className="flex flex-col gap-1">
          <h3 className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle">
            {t('automation.deploys.untested')}
          </h3>
          <p className="text-[12px] text-ink-subtle">{t('automation.deploys.untestedHint')}</p>
          <ul className="flex flex-col gap-0.5">
            {untested.map((m) => (
              <MethodLine key={m.name} method={m} />
            ))}
          </ul>
        </div>
      ) : null}
      {deploy.linkedBy === 'file' && deploy.methods.length ? (
        <details className="text-[12px] text-ink-muted">
          <summary className="cursor-pointer">
            {t('automation.deploys.methodsByFile', { count: deploy.methods.length })}
          </summary>
          <p className="mt-1 text-ink-subtle">{t('automation.deploys.methodsByFileHint')}</p>
          <ul className="mt-1 flex flex-col gap-0.5">
            {deploy.methods.map((m) => (
              <MethodLine key={m.name} method={m} />
            ))}
          </ul>
        </details>
      ) : null}
      {loud.length ? (
        <div className="flex flex-col gap-1">
          <h3 className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle">
            {t('automation.deploys.notApi')}
          </h3>
          <ul className="flex flex-col gap-0.5 text-[12px]">
            {loud.map((kind) => (
              <li key={kind}>
                <span className="text-ink">
                  {t(`automation.deploys.note.${kind}`)} · {groups.get(kind)?.length}
                </span>{' '}
                <span className="break-words font-mono text-[11px] text-ink-subtle">
                  {sample(kind)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {quiet.length ? (
        <p className="text-[12px] text-ink-subtle">
          {t('automation.deploys.quiet')}{' '}
          {quiet
            .map((kind) =>
              sample(kind)
                ? `${t(`automation.deploys.note.${kind}`)}: ${sample(kind)}`
                : t(`automation.deploys.note.${kind}`),
            )
            .join('; ')}
        </p>
      ) : null}
    </>
  );
};

const DeployCard = ({ deploy }: { deploy: AutomationDeployDto }) => {
  const { t } = useT();
  const { relative } = useFormat();
  const { errorText } = useErrorText();
  const { can } = usePermissions();
  const queryClient = useQueryClient();
  const run = useMutation({
    mutationFn: () => api.post<AutomationDeployDto>(`/automation/deploys/${deploy.id}/run`),
    onSuccess: (result) => {
      // a project that could not start says why on the card; the toast only says whether anything ran
      if (result.state === 'started') toast.success(t('automation.deploys.runStarted'));
      else toast.error(t('automation.deploys.runFailed'));
      void queryClient.invalidateQueries({ queryKey: deploysKey });
    },
    onError: (error) => toast.error(errorText(error)),
  });

  return (
    <Card>
      <div className="flex flex-col gap-3 px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[15px] font-semibold text-ink">{deploy.service}</span>
            <span className="text-[12px] text-ink-subtle">
              {deploy.branch} · {deploy.headSha.slice(0, 8)} · {relative(deploy.receivedAt)}
            </span>
            {deploy.tasks.map((task) => (
              <Badge key={task} className="bg-sunken text-ink-muted">
                {task}
              </Badge>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {deploy.deploy?.url ? (
              <a
                href={deploy.deploy.url}
                target="_blank"
                rel="noreferrer"
                className="text-[12px] text-accent hover:underline"
              >
                {t('automation.deploys.deploy', { status: deploy.deploy.status })}
              </a>
            ) : (
              <span className="text-[12px] text-ink-subtle">
                {deploy.deploy
                  ? t('automation.deploys.deploy', { status: deploy.deploy.status })
                  : t('automation.deploys.deployUnknown')}
              </span>
            )}
            <Badge className={STATE_STYLES[deploy.state]}>
              {t(`automation.deploys.state.${deploy.state}`)}
            </Badge>
            {deploy.state === 'notStarted' && can('automation:run') ? (
              <Button size="sm" onClick={() => run.mutate()} loading={run.isPending}>
                {run.isPending ? null : <Play className="size-3.5" />}
                {t('automation.deploys.run')}
              </Button>
            ) : null}
          </div>
        </div>

        <details className="text-[12px] text-ink-muted">
          <summary className="cursor-pointer">
            {t('automation.deploys.commits', { count: deploy.commits.length })}
            {deploy.startedBy
              ? ` · ${t('automation.deploys.startedBy', { who: deploy.startedBy })}`
              : ''}
          </summary>
          <ul className="mt-1 flex flex-col gap-0.5 pl-3">
            {deploy.commits.map((c) => (
              <li key={c.sha}>
                <span className="font-mono text-ink-subtle">{c.sha.slice(0, 8)}</span> {c.subject}
                {c.author ? <span className="text-ink-subtle"> — {c.author}</span> : null}
              </li>
            ))}
          </ul>
        </details>

        {deploy.findings.length ? (
          <div className="flex flex-col gap-1">
            <h3 className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle">
              {t('automation.deploys.findings')}
            </h3>
            <ul className="flex flex-col gap-0.5 text-[13px] text-ink">
              {deploy.findings.slice(0, 6).map((f, i) => (
                <li key={`${f.title}-${i}`}>
                  {f.title}
                  {f.file ? <span className="text-[12px] text-ink-subtle"> · {f.file}</span> : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {deploy.state === 'noTests' ? (
          <p className="text-[13px] text-block">
            {deploy.covering.length
              ? t('automation.deploys.noTests', { list: deploy.covering.join(', ') })
              : t('automation.deploys.noTestsNoCover')}
          </p>
        ) : null}

        {deploy.runs
          .filter((r) => r.error)
          .map((r) => (
            <p key={r.projectId} className="text-[13px] text-fail">
              {t('automation.deploys.runError', { project: r.projectId, error: r.error ?? '' })}
            </p>
          ))}

        {deploy.classes.length ? (
          <div className="flex flex-col gap-1">
            <h3 className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle">
              {t('automation.deploys.classes')}
            </h3>
            <ul className="divide-y divide-border rounded-md border border-border">
              {deploy.classes.map((item) => (
                <ClassRow key={`${item.projectId}|${item.className}`} item={item} deploy={deploy} />
              ))}
            </ul>
          </div>
        ) : null}

        <Reach deploy={deploy} />

        {deploy.manualCases.length ? (
          <div className="flex flex-col gap-1">
            <h3 className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle">
              {t('automation.deploys.manual')}
            </h3>
            <div className="flex flex-wrap gap-1.5">
              {deploy.manualCases.map((c) => (
                <Link
                  key={c.caseKey}
                  to="/projects/$projectCode/test-cases/$caseKey"
                  params={{ projectCode: c.projectCode, caseKey: c.caseKey }}
                  className="inline-flex items-center gap-1 rounded-md bg-sunken px-1.5 py-0.5 text-[12px] text-ink hover:underline"
                  title={c.folder ?? undefined}
                >
                  <EntityKey>{c.caseKey}</EntityKey>
                  <span className="max-w-64 truncate">{c.title}</span>
                </Link>
              ))}
            </div>
          </div>
        ) : null}

        {deploy.possible.length ? (
          <details className="text-[12px] text-ink-muted">
            <summary className="cursor-pointer">
              {t('automation.deploys.possible')} · {deploy.possible.length}
            </summary>
            <ul className="mt-1 divide-y divide-border rounded-md border border-border">
              {deploy.possible.map((item) => (
                <ClassRow key={`${item.projectId}|${item.className}`} item={item} deploy={deploy} />
              ))}
            </ul>
          </details>
        ) : null}

        {deploy.unresolved.length ? (
          <p className="text-[12px] text-ink-subtle">
            {t('automation.deploys.unresolved', {
              list: [...new Set(deploy.unresolved.map((u) => `${u.repo}/${u.className}`))].join(
                ', ',
              ),
            })}
          </p>
        ) : null}
      </div>
    </Card>
  );
};

/** «Выкатки на stage»: what each backend change reaches, newest first. */
export const DeploysPage = () => {
  const { t } = useT();
  const deploys = useQuery({
    queryKey: deploysKey,
    queryFn: ({ signal }) =>
      api.get<AutomationDeployDto[]>('/automation/deploys', undefined, signal),
    refetchInterval: REFRESH_MS,
  });
  return (
    <>
      <PageHeader
        title={t('automation.deploys.title')}
        description={t('automation.deploys.hint')}
      />
      {deploys.error ? (
        <ErrorState error={deploys.error} onRetry={() => void deploys.refetch()} />
      ) : !deploys.data ? (
        <SkeletonRows rows={4} />
      ) : deploys.data.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Rocket className="size-6" />}
            title={t('automation.deploys.empty')}
            description={t('automation.deploys.emptyHint')}
          />
        </Card>
      ) : (
        <div className="flex flex-col gap-4">
          {deploys.data.map((deploy) => (
            <DeployCard key={deploy.id} deploy={deploy} />
          ))}
        </div>
      )}
    </>
  );
};
